"use client";

import type { ComponentType } from "react";
import {
	Button,
	Chip as ChipComponent,
	type ChipProps
} from "@pamperedchef/design-system";

import { className as classNameHelper } from "@/composites/foundation/utilities/className";

import type { AppliedFilter } from "../types";

import * as styles from "./FilterChips.module.scss";

const Chip = ChipComponent as unknown as ComponentType<ChipProps>;

const DEFAULT_MAX_LABEL_LENGTH = 28;

export interface FilterChipsLabels {
	region: string;
	clearAll: string;
	remove: (label: string) => string;
}

const DEFAULT_LABELS: FilterChipsLabels = {
	region: "Applied filters",
	clearAll: "Clear All",
	remove: (label) => `Remove ${label}`
};

export interface FilterChipsProps {
	applied: AppliedFilter[];
	onRemove: (filter: AppliedFilter) => void;
	onClearAll?: (filters: AppliedFilter[]) => void;
	labels?: Partial<FilterChipsLabels>;
	maxLabelLength?: number;
	className?: string;
}

export function FilterChips({
	applied,
	onRemove,
	onClearAll,
	labels,
	maxLabelLength = DEFAULT_MAX_LABEL_LENGTH,
	className
}: FilterChipsProps): React.JSX.Element | null {
	if (applied.length === 0) return null;

	const text = { ...DEFAULT_LABELS, ...labels };

	return (
		<div
			role="group"
			aria-label={text.region}
			{...classNameHelper(styles.chips, className)}
			data-component="FilterChips"
		>
			<ul className={styles.list}>
				{applied.map((filter) => (
					<li
						key={`${filter.groupId}:${filter.value}`}
						className={styles.item}
					>
						<Chip
							kind="filled"
							size="md"
							selected
							label={shortenLabel(filter.label, maxLabelLength)}
							title={filter.label}
							aria-label={text.remove(filter.label)}
							onPress={() => onRemove(filter)}
						/>
					</li>
				))}
				{onClearAll ? (
					<li className={styles.item}>
						<Button
							kind="tertiary"
							size="sm"
							onPress={() => onClearAll(applied)}
						>
							{text.clearAll}
						</Button>
					</li>
				) : null}
			</ul>
		</div>
	);
}

function shortenLabel(label: string, maxLength: number): string {
	if (label.length <= maxLength) return label;
	const cut = label.slice(0, maxLength - 1);
	const lastSpace = cut.lastIndexOf(" ");
	return `${(lastSpace > 0 ? cut.slice(0, lastSpace) : cut).trimEnd()}…`;
}
